import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { CaseFormComponent } from './components/case-form/case-form.component';
import { CaseStartedComponent } from './components/case-started/case-started.component';
import {
  CashbookLoginFailedComponent
} from './components/cashbook/cashbook-login-failed/cashbook-login-failed.component';
import {
  CashbookLoginSuccessComponent
} from './components/cashbook/cashbook-login-success/cashbook-login-success.component';
import { ClaimsRerouterComponent } from './components/claims-rerouter/claims-rerouter.component';
import { CasesComponent } from './components/dashboard/cases/cases.component';
import { EventsComponent } from './components/dashboard/events/events.component';
import { ClientProfileComponent } from './components/dashboard/client-profile/client-profile.component';
import { DashboardComponent } from './components/dashboard/dashboard.component';
import { UserProfileComponent } from './components/dashboard/user-profile/user-profile.component';
import { CreditPageComponent } from './components/credit-page/credit-page.component';
import { EhazSsoErrorComponent } from './components/ehaz/ehaz-sso-error/ehaz-sso-error.component';
import { EhazSsoComponent } from './components/ehaz/ehaz-sso/ehaz-sso.component';
import {
  GetPasswordLinkSuccessComponent
} from './components/forgot-password/get-password-link-success/get-password-link-success.component';
import { GetPasswordLinkComponent } from './components/forgot-password/get-password-link/get-password-link.component';
import {
  NewPasswordSuccessComponent
} from './components/forgot-password/new-password-success/new-password-success.component';
import { NewPasswordComponent } from './components/forgot-password/new-password/new-password.component';
import { LoginComponent } from './components/login/login/login.component';
import { RegisterComponent } from './components/login/register/register.component';
import { SignupFinalizationComponent } from './components/login/signup-finalization/signup-finalization.component';
import { TwofaFormComponent } from './components/login/twofa-form/twofa-form.component';
import { VerifyEmailComponent } from './components/login/verify-email/verify-email.component';
import { VerifyTokenExpiredComponent } from './components/login/verify-token-expired/verify-token-expired.component';
import {
  SzamlazzhuSsoErrorComponent
} from "./components/szamlazzhu/szamlazzhu-sso-error/szamlazzhu-sso-error.component";
import {
  SzamlazzhuSsoResponseComponent
} from "./components/szamlazzhu/szamlazzhu-sso-response/szamlazzhu-sso-response.component";
import { SzamlazzhuSsoComponent } from "./components/szamlazzhu/szamlazzhu-sso/szamlazzhu-sso.component";
import { AuthGuardService } from './services/auth-guard/auth-guard.service';
import { caseFormTitleResolver as resolveCaseFormTitle } from './resolvers/case-form-title.resolver';
import { LoadingScreenComponent } from './shared/loading-screen/loading-screen.component';
import { translateTitle as resolveTranslateTitle } from './shared/services/title/translate-title.resolver';
import { withTestTitlePrefix } from './shared/services/title/with-test-title-prefix.resolver';
import { UserGuideComponent } from './user-guide/user-guide.component';
import { SsoRedirectComponent as GoogleSsoRedirectComponent } from './components/google/sso-redirect/sso-redirect.component';

const translateTitle = (key: string) => withTestTitlePrefix(resolveTranslateTitle(key));
const caseFormTitleResolver = withTestTitlePrefix(resolveCaseFormTitle);

const routes: Routes = [
  // Form Routes
  // Legacy routes
  { path: 'case/:uuid/data-entry', redirectTo: 'case/:uuid/debtor' },
  { path: 'case/:uuid/summary', redirectTo: 'case/:uuid/payment' },
  { path: 'cases/wizard', redirectTo: '/case' },
  // Form Init route
  {
    path: 'case',
    component: CaseFormComponent,
    title: caseFormTitleResolver,
  },
  // Form Steps
  {
    path: 'case/:uuid',
    pathMatch: 'full',
    redirectTo: 'case/:uuid/',
  },
  {
    path: 'case/:uuid/:step',
    component: CaseFormComponent,
    title: caseFormTitleResolver,
  },
  {
    path: 'user/add-new-client-onboarding',
    redirectTo: '/case',
  },
  {
    path: 'user/add-new-client',
    redirectTo: '/case',
  },
  {
    path: 'user/add-new-client-success',
    redirectTo: '/case',
  },
  { path: 'user/case-started', component: CaseStartedComponent, title: translateTitle('BROWSER_TITLES.LOADING') },
  {
    path: 'user',
    component: DashboardComponent,
    canActivate: [AuthGuardService],
    children: [
      // {path: 'api', component: ApiConnectionComponent},
      {
        path: 'user-profile',
        component: UserProfileComponent,
        title: translateTitle('USER_PROFILE.HEADER.TITLE'),
      },
      {
        path: 'client-profile',
        component: ClientProfileComponent,
        title: translateTitle('BROWSER_TITLES.PROFILE'),
      },
      { path: 'credits', component: CreditPageComponent, title: translateTitle('CREDITS.HEADER.TITLE') },
      // { path: 'campaign', component: CampaignComponent },
      { path: 'edit-case/:id', redirectTo: '/login' },
      {
        path: 'cases',
        component: CasesComponent,
        title: translateTitle('BROWSER_TITLES.CASES'),
      },
      {
        path: 'events',
        component: EventsComponent,
        title: translateTitle('BROWSER_TITLES.EVENTS'),
      },
      { path: '**', redirectTo: 'cases' }
    ]
  },

  {
    path: 'verify-email',
    component: VerifyEmailComponent,
    title: translateTitle('BROWSER_TITLES.SIGNUP'),
  },
  {
    path: 'verify-token-expired',
    component: VerifyTokenExpiredComponent,
    title: translateTitle('BROWSER_TITLES.SIGNUP'),
  },
  {
    path: 'signup-finalization',
    component: SignupFinalizationComponent,
    title: translateTitle('BROWSER_TITLES.SIGNUP'),
  },
  {
    path: 'forgot-password-get-link',
    component: GetPasswordLinkComponent,
    title: translateTitle('BROWSER_TITLES.PASSWORD_RESET'),
  },
  {
    path: 'forgot-password-get-link-success',
    component: GetPasswordLinkSuccessComponent,
    title: translateTitle('BROWSER_TITLES.PASSWORD_RESET'),
  },
  {
    path: 'forgot-password-new-password/:token',
    component: NewPasswordComponent,
    title: translateTitle('BROWSER_TITLES.PASSWORD_RESET'),
  },
  {
    path: 'forgot-password-new-password-success',
    component: NewPasswordSuccessComponent,
    title: translateTitle('BROWSER_TITLES.PASSWORD_RESET'),
  },
  { path: 'login', component: LoginComponent, title: translateTitle('BROWSER_TITLES.LOGIN') },
  { path: 'logout-success', redirectTo: '/login' },
  {
    path: '2fa',
    children: [
      { path: '', redirectTo: 'verify', pathMatch: 'full' },
      {
        path: 'verify',
        component: TwofaFormComponent,
        title: translateTitle('BROWSER_TITLES.TWO_FACTOR'),
      },
    ],
  },
  {
    path: 'signup',
    component: RegisterComponent,
    title: translateTitle('BROWSER_TITLES.SIGNUP'),
  },
  { path: 'beta-signup', redirectTo: 'signup' },
  {
    path: '_oauth',
    children: [
      { path: 'cashbook', redirectTo: '/login' },
      {
        path: 'cashbook_login_success',
        component: CashbookLoginSuccessComponent,
        title: translateTitle('BROWSER_TITLES.LOGIN'),
      },
      {
        path: 'cashbook_login_failed',
        component: CashbookLoginFailedComponent,
        title: translateTitle('BROWSER_TITLES.LOGIN'),
      },
      { path: '**', redirectTo: '/login' }
    ]
  },
  {
    path: 'claims/:claimToken',
    component: ClaimsRerouterComponent,
    title: translateTitle('BROWSER_TITLES.LOADING'),
  },
  { path: 'sso/ehaz', component: EhazSsoComponent, title: translateTitle('BROWSER_TITLES.LOGIN') },
  {
    path: 'sso/ehaz/error',
    component: EhazSsoErrorComponent,
    title: translateTitle('BROWSER_TITLES.LOGIN'),
  },
  { path: 'sso/google', component: GoogleSsoRedirectComponent, title: translateTitle('BROWSER_TITLES.LOGIN') },
  { path: 'sso/szamlazzhu', component: SzamlazzhuSsoComponent, title: translateTitle('BROWSER_TITLES.LOGIN') },
  {
    path: 'sso/szamlazzhu/error',
    component: SzamlazzhuSsoErrorComponent,
    title: translateTitle('BROWSER_TITLES.LOGIN'),
  },
  {
    path: 'sso/szamlazzhu/response',
    component: SzamlazzhuSsoResponseComponent,
    title: translateTitle('BROWSER_TITLES.LOGIN'),
  },
  { path: 'loading', component: LoadingScreenComponent, title: translateTitle('BROWSER_TITLES.LOADING') },
  {
    path: 'user-guide',
    component: UserGuideComponent,
    canActivate: [AuthGuardService],
    title: translateTitle('BROWSER_TITLES.USER_GUIDE'),
  },
  { path: '**', redirectTo: 'user' },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { anchorScrolling: 'enabled' })],
  exports: [RouterModule]
})

export class AppRoutingModule {
}
